<script setup lang="ts">
import type { Leg, ResultsLegProperties } from '@optionway/models';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.resultsLeg;

const properties = withDefaults(
  defineProps<
    ResultsLegProperties &
      Leg & {
        loading?: boolean;
        compact?: boolean;
        hideBorderBottom?: boolean;
        hideOutline?: boolean;
        hideTooltips?: boolean;
      }
  >(),
  { hidePin: undefined, hideBags: undefined, hideDate: undefined },
);
const componentConfig = computed<ResultsLegProperties>(() => {
  return {
    hidePin: properties.hidePin ?? config?.hidePin ?? false,
    hideBags: properties.hideBags ?? config?.hideBags ?? false,
    hideDate: properties.hideDate ?? config?.hideDate ?? false,
    components: properties.components ?? config?.components,
    hideComponents: properties.hideComponents ?? config?.hideComponents,
  };
});

const locationLabelsStore = useLocationLabels();
</script>

<template>
  <div
    class="flex w-full flex-col @container md:rounded-2xl md:border-b-0 md:pb-0"
    :class="{
      'border-b border-gray-300 pb-2': !properties.hideBorderBottom,
      'md:outline md:outline-1 md:outline-offset-0 md:outline-border': !properties.hideOutline,
    }"
  >
    <div class="flex flex-1 items-center gap-2 self-stretch @md:p-2">
      <div class="flex w-full flex-col gap-3 @md:flex-row @md:gap-0 @md:space-x-5">
        <div class="flex justify-between @md:items-center @md:justify-start">
          <!-- Date / Airline -->
          <ResultsLegDateAirline
            :loading="loading"
            :departure-date-time="properties.departureDateTime"
            :carrier="properties.carrier"
            :hide-date="componentConfig.hideDate"
            :hide-tooltips="properties.hideTooltips"
            :compact="properties.compact"
          />

          <ResultsBagAllowancesWrapper
            v-if="!componentConfig.hideBags"
            :loading="loading"
            :bag-allowances="properties.bagAllowances"
            class="@md:hidden"
          />
        </div>

        <!-- Summary -->
        <ResultsLegSummary>
          <template #departure>
            <ResultsLegEndpoint
              :loading="loading"
              :date-time="properties.departureDateTime"
              :iata-code="properties.departure?.iataCode"
              :city-label="locationLabelsStore.city(properties.departure?.cityIataCode)"
              :airport-label="locationLabelsStore.airport(properties.departure?.iataCode)"
              :display-type="properties.departure?.displayType"
              :hide-tooltips="properties.hideTooltips"
              :compact="properties.compact"
            />
          </template>
          <template
            v-if="properties.beforeStopoverTransportType"
            #departureLine
          >
            <div class="mx-2 flex flex-1 items-center gap-1">
              <UiCustomSeparatorLine
                width-class="flex-1"
                color-class="bg-dark-blue-700"
              />
              <ResultsLegTransportIcon :type="properties.beforeStopoverTransportType" />
              <UiCustomSeparatorLine
                width-class="flex-1"
                color-class="bg-dark-blue-700"
              />
            </div>
          </template>
          <template #stopoverInfo>
            <ResultsLegStopoverInfo
              :loading="properties.loading"
              :stopover="properties.stopover"
              :duration="properties.duration"
              :has-technical-stop="properties.withTechnical ?? false"
              :hide-tooltips="properties.hideTooltips"
            />
          </template>
          <template
            v-if="properties.afterStopoverTransportType"
            #arrivalLine
          >
            <div class="mx-2 flex flex-1 items-center gap-1">
              <UiCustomSeparatorLine
                width-class="flex-1"
                color-class="bg-dark-blue-700"
              />
              <ResultsLegTransportIcon :type="properties.afterStopoverTransportType" />
              <UiCustomSeparatorLine
                width-class="flex-1"
                color-class="bg-dark-blue-700"
              />
            </div>
          </template>
          <template #arrival>
            <ResultsLegEndpoint
              :loading="loading"
              :date-time="properties.arrivalDateTime"
              :iata-code="properties.arrival?.iataCode"
              :city-label="locationLabelsStore.city(properties.arrival?.cityIataCode)"
              :airport-label="locationLabelsStore.airport(properties.arrival?.iataCode)"
              :display-type="properties.arrival?.displayType"
              :day-offset="properties.arrivalDayOffset"
              :hide-tooltips="properties.hideTooltips"
              :compact="properties.compact"
            />
          </template>
        </ResultsLegSummary>

        <ResultsBagAllowancesWrapper
          v-if="!componentConfig.hideBags"
          :loading="loading"
          :bag-allowances="properties.bagAllowances"
          class="hidden @md:flex"
        />
      </div>
    </div>
  </div>
</template>
